/**
 * FeedingTicks — one-tap meal outcomes for today's slots (migration 0101).
 *
 * Mounted in the Today occupant drawer. Loads the booking's resolved plan +
 * today's log, renders a row per slot with Eaten / Partial / Refused (and a
 * quieter Skipped) buttons, and POSTs to /api/bookings/:id/feeding-log on
 * tap. A second tap corrects the outcome in place (the API upserts per
 * slot). When a pet has no structured plan the row set falls back to the
 * four standard slots so a meal can still be logged.
 */
import {
  FEEDING_OUTCOME_LABELS,
  type FeedingLogEntry,
  type FeedingOutcome,
  type FeedingPlanItem,
  type FeedingSlot,
  feedingSlotKey,
  feedingSlotLabel,
  formatFeedingItem,
  getBookingFeedingLog,
  recordFeedingOutcome,
} from '@/lib/feeding';
import { Check, CircleSlash, Loader2, Minus, Utensils, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const FALLBACK_SLOTS: FeedingPlanItem[] = [
  { slot: 'morning', food: '', quantity: '', unit: 'other' },
  { slot: 'evening', food: '', quantity: '', unit: 'other' },
];

const OUTCOME_STYLE: Record<FeedingOutcome, { active: string; icon: typeof Check }> = {
  eaten: { active: 'bg-success/15 text-success ring-success/30', icon: Check },
  partial: {
    active: 'bg-warning/15 text-warning-foreground ring-warning/30',
    icon: Minus,
  },
  refused: { active: 'bg-destructive/10 text-destructive ring-destructive/25', icon: X },
  skipped: { active: 'bg-secondary text-muted-foreground ring-border', icon: CircleSlash },
};

const PRIMARY_OUTCOMES: FeedingOutcome[] = ['eaten', 'partial', 'refused'];

interface FeedingTicksProps {
  bookingId: string;
  /** Show the Skipped button too (default: only eaten / partial / refused). */
  showSkipped?: boolean;
}

export function FeedingTicks({ bookingId, showSkipped = false }: FeedingTicksProps) {
  const [slots, setSlots] = useState<FeedingPlanItem[] | null>(null);
  const [entries, setEntries] = useState<FeedingLogEntry[]>([]);
  const [today, setToday] = useState<string>('');
  const [applies, setApplies] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getBookingFeedingLog(bookingId);
      setApplies(res.feedingApplies);
      setSlots(res.slots.length > 0 ? res.slots : FALLBACK_SLOTS);
      setEntries(res.entries);
      setToday(res.today);
    } catch (e) {
      setError((e as Error).message || 'Could not load feeding log');
      setSlots(FALLBACK_SLOTS);
    }
  }, [bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const byKey = new Map(entries.map((e) => [feedingSlotKey(e), e]));

  async function tap(item: FeedingPlanItem, outcome: FeedingOutcome) {
    const key = feedingSlotKey(item);
    setPending(`${key}:${outcome}`);
    setError(null);
    try {
      const res = await recordFeedingOutcome(bookingId, {
        slot: item.slot as FeedingSlot,
        slotTime: item.time ?? null,
        outcome,
        ...(today ? { scheduledFor: today } : {}),
      });
      setEntries((prev) => {
        const next = prev.filter((e) => e.id !== res.entry.id && feedingSlotKey(e) !== key);
        return [...next, res.entry];
      });
    } catch (e) {
      setError((e as Error).message || 'Could not record');
    } finally {
      setPending(null);
    }
  }

  if (!applies) return null;

  return (
    <section className="mt-4" data-testid="feeding-ticks">
      <div className="mb-2 flex items-center gap-2">
        <Utensils className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
        <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Feeding today
        </p>
        {today && <span className="num ml-auto text-[11px] text-muted-foreground">{today}</span>}
      </div>
      {slots === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <ul className="divide-y divide-border">
          {slots.map((item) => {
            const key = feedingSlotKey(item);
            const current = byKey.get(key);
            const outcomes = showSkipped
              ? [...PRIMARY_OUTCOMES, 'skipped' as const]
              : PRIMARY_OUTCOMES;
            return (
              <li key={key} className="flex items-center gap-3 py-2" data-testid="feeding-slot-row">
                <div className="min-w-0 flex-1">
                  <p className="num text-sm font-medium">{feedingSlotLabel(item)}</p>
                  {item.food && (
                    <p className="truncate text-xs text-muted-foreground">
                      {formatFeedingItem(item)}
                    </p>
                  )}
                </div>
                <div
                  className="flex shrink-0 gap-1"
                  role="group"
                  aria-label={`${feedingSlotLabel(item)} outcome`}
                >
                  {outcomes.map((o) => {
                    const { active, icon: Icon } = OUTCOME_STYLE[o];
                    const isActive = current?.outcome === o;
                    const isPending = pending === `${key}:${o}`;
                    return (
                      <button
                        key={o}
                        type="button"
                        onClick={() => void tap(item, o)}
                        disabled={pending !== null}
                        aria-pressed={isActive}
                        aria-label={`${feedingSlotLabel(item)}: ${FEEDING_OUTCOME_LABELS[o]}`}
                        title={FEEDING_OUTCOME_LABELS[o]}
                        className={`press inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ${
                          isActive
                            ? active
                            : 'ring-hairline bg-background text-muted-foreground hover:bg-secondary'
                        } disabled:opacity-60`}
                      >
                        {isPending ? (
                          <Loader2 className="size-3 animate-spin" strokeWidth={2} />
                        ) : (
                          <Icon className="size-3" strokeWidth={2.25} />
                        )}
                        {FEEDING_OUTCOME_LABELS[o]}
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </section>
  );
}
